import { HttpApiError, HttpApiGroup } from "@effect/platform";
import { Schema } from "effect";
import { endpoint } from "../auth/endpoint";
import { DataIntegrityError, DatabaseError } from "../auth/errors";

import { ConversationId, MacroId, SupportTicketId } from "../ids/definitions";
import {
  SupportTicketSnoozeOptions,
  type SupportTicketStatus,
} from "../models/shopper-ai/conversation/support-ticket";

// Subset of SupportTicketStatus values a macro is allowed to assign. Lifecycle
// statuses (HIDDEN, ARCHIVED, DELETED, DRAFT) are intentionally excluded.
export const MACRO_STATUS_TO_SET_VALUES = [
  "OPEN",
  "CLOSED",
  "SNOOZED",
  "IN_PROGRESS",
] as const satisfies readonly SupportTicketStatus[];
export const MacroStatusToSet = Schema.Literal(...MACRO_STATUS_TO_SET_VALUES);
export type MacroStatusToSet = typeof MacroStatusToSet.Type;

export const MACRO_STATUS_TO_SET_LABELS: Record<MacroStatusToSet, string> = {
  OPEN: "Open",
  CLOSED: "Closed",
  SNOOZED: "Snoozed",
  IN_PROGRESS: "In progress",
};

export const MacroAutomationKind = {
  SET_STATUS: "setStatus",
  ADD_TAGS: "addTags",
  CHANGE_EMAIL_SUBJECT: "changeEmailSubject",
  ADD_INTERNAL_NOTE: "addInternalNote",
  FORWARD_MESSAGE: "forwardMessage",
} as const;
export type MacroAutomationKind =
  (typeof MacroAutomationKind)[keyof typeof MacroAutomationKind];
export const MacroAutomationKindSchema = Schema.Literal(
  ...Object.values(MacroAutomationKind),
);

export const NoteToAdd = Schema.Struct({
  content: Schema.String,
  htmlContent: Schema.String,
  usersMentioned: Schema.NullOr(Schema.Array(Schema.String)),
});
export type NoteToAdd = typeof NoteToAdd.Type;

export const MessageToForward = Schema.Struct({
  emailAddress: Schema.String,
  extraContent: Schema.NullOr(Schema.String),
});
export type MessageToForward = typeof MessageToForward.Type;

export const Macro = Schema.Struct({
  id: MacroId,
  name: Schema.String,
  content: Schema.String,
  htmlContent: Schema.String,
  tagsToAdd: Schema.NullOr(Schema.Array(Schema.String)),
  statusToSet: Schema.NullOr(MacroStatusToSet),
  snoozeDuration: Schema.NullOr(SupportTicketSnoozeOptions),
  emailSubjectToSet: Schema.NullOr(Schema.String),
  noteToAdd: Schema.NullOr(NoteToAdd),
  messageToForward: Schema.NullOr(MessageToForward),
});
export type Macro = typeof Macro.Type;

const MacroWritableFields = {
  name: Schema.String,
  content: Schema.String,
  htmlContent: Schema.String,
  tagsToAdd: Schema.NullOr(Schema.Array(Schema.String)),
  statusToSet: Schema.NullOr(MacroStatusToSet),
  snoozeDuration: Schema.NullOr(SupportTicketSnoozeOptions),
  emailSubjectToSet: Schema.NullOr(Schema.String),
  noteToAdd: Schema.NullOr(NoteToAdd),
  messageToForward: Schema.NullOr(MessageToForward),
};

export const CreateMacroPayload = Schema.Struct(MacroWritableFields);
export type CreateMacroPayload = typeof CreateMacroPayload.Type;

export const UpdateMacroPayload = Schema.Struct(MacroWritableFields);
export type UpdateMacroPayload = typeof UpdateMacroPayload.Type;

const MacroResponse = Schema.Struct({ macro: Macro });

const GetMacrosResponse = Schema.Struct({ macros: Schema.Array(Macro) });

/**
 * Optional scoping for the macro list. When `supportTicketId` is provided the
 * server returns the macros for the *ticket's* account (resolved through
 * connected-team access) rather than the caller's home-team account. Omitting
 * it preserves the legacy caller-account behavior for non-ticket contexts.
 */
export const GetMacrosUrlParams = Schema.Struct({
  supportTicketId: Schema.optional(SupportTicketId),
});
export type GetMacrosUrlParams = typeof GetMacrosUrlParams.Type;

const ReplaceMacroVariablesPayloadBase = {
  content: Schema.String,
  timezone: Schema.optional(Schema.String),
};

export const ReplaceMacroVariablesPayload = Schema.Union(
  Schema.Struct({
    ...ReplaceMacroVariablesPayloadBase,
    conversationId: ConversationId,
  }),
  Schema.Struct({
    ...ReplaceMacroVariablesPayloadBase,
    supportTicketId: SupportTicketId,
  }),
);
export type ReplaceMacroVariablesPayload =
  typeof ReplaceMacroVariablesPayload.Type;

const ReplaceMacroVariablesResponse = Schema.Struct({
  message: Schema.String,
  valuesExistForAllVariables: Schema.Boolean,
});

export const MacroGroup = HttpApiGroup.make("macro")
  .add(
    endpoint.merchant
      .get("getMacros", "/macros")
      .setUrlParams(GetMacrosUrlParams)
      .addSuccess(GetMacrosResponse)
      .addError(HttpApiError.NotFound)
      .addError(HttpApiError.Forbidden)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .post("replaceMacroVariables", "/macros/replace-variables")

      .setPayload(ReplaceMacroVariablesPayload)
      .addSuccess(ReplaceMacroVariablesResponse)
      .addError(HttpApiError.Forbidden)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .post("createMacro", "/macros")

      .setPayload(CreateMacroPayload)
      .addSuccess(MacroResponse)
      .addError(HttpApiError.BadRequest)
      .addError(HttpApiError.Conflict)
      .addError(HttpApiError.Forbidden)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .patch("updateMacro", "/macros/:id")

      .setPath(Schema.Struct({ id: MacroId }))
      .setPayload(UpdateMacroPayload)
      .addSuccess(MacroResponse)
      .addError(HttpApiError.BadRequest)
      .addError(HttpApiError.Conflict)
      .addError(HttpApiError.NotFound)
      .addError(HttpApiError.Forbidden)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .del("deleteMacro", "/macros/:id")

      .setPath(Schema.Struct({ id: MacroId }))
      .addSuccess(Schema.Void)
      .addError(HttpApiError.NotFound)
      .addError(HttpApiError.Forbidden)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .prefix("/api");
